import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";

/** Map of guidance action `code` → the handler a detail page wires up. */
export type ActionHandlers = Record<string, () => void>;

type HandlersGetter = () => ActionHandlers;

interface MaxwellActionsCtx {
  /** Register a subject's handlers; returns an unregister fn. */
  register: (subjectKey: string, getter: HandlersGetter) => () => void;
  /** Dispatch a guidance action `code` to whatever's registered for the
   *  subject. Unknown subject/code is a silent no-op (the entity may not be
   *  open). */
  dispatch: (subjectKey: string, code: string) => void;
}

const Ctx = createContext<MaxwellActionsCtx | null>(null);

/**
 * Lets a detail page register guidance-CTA handlers keyed by subject, so the
 * globally-mounted Maxwell panel can dispatch a `code` to the right page. The
 * registry holds a *getter* per subject (not the handler object) so a page's
 * fresh-each-render closures stay current without re-registering.
 */
export function MaxwellActionProvider({ children }: { children: ReactNode }) {
  const registryRef = useRef<Map<string, HandlersGetter>>(new Map());

  const register = useCallback((subjectKey: string, getter: HandlersGetter) => {
    registryRef.current.set(subjectKey, getter);
    return () => {
      // Only delete if we still own the slot (a remount may have replaced it).
      if (registryRef.current.get(subjectKey) === getter) {
        registryRef.current.delete(subjectKey);
      }
    };
  }, []);

  const dispatch = useCallback((subjectKey: string, code: string) => {
    const getter = registryRef.current.get(subjectKey);
    const fn = getter?.()[code];
    if (fn) fn();
  }, []);

  const value = useMemo<MaxwellActionsCtx>(
    () => ({ register, dispatch }),
    [register, dispatch],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMaxwellActions(): MaxwellActionsCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useMaxwellActions must be used within <MaxwellActionProvider>");
  }
  return ctx;
}

/**
 * Register a page's guidance-action handlers for `subjectKey` (e.g.
 * `deal:<id>`), keeping them fresh via a ref so re-renders don't churn the
 * registration. Unregisters on unmount.
 */
export function useRegisterMaxwellActions(subjectKey: string, handlers: ActionHandlers) {
  const { register } = useMaxwellActions();
  const handlersRef = useRef(handlers);

  // Keep the ref current without writing to it during render. Dispatch fires
  // from user interaction (well after commit), so it always reads fresh.
  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    return register(subjectKey, () => handlersRef.current);
  }, [subjectKey, register]);
}
